import { useEffect, useMemo, useRef, useState } from 'react';
import { useRelatedContentAuxiliar } from '../../../../../modules/Disciplines/hooks/useRelatedContentAux';
import { useShouldShowPopUpScore } from '../../../../hooks/useShouldShowPopUpScore';
import { useFetchLessonQuiz } from '../../../../hooks/useFetchLessonQuiz';
import { useUserStore } from '../../../../../modules/Auth/stores/authStore';
import useGetBadgeScores from '../../../../../modules/Disciplines/hooks/useGetBadgeScores';
import useFlashcardByVideoAula from '../../../../../modules/Memory/hooks/use-flashcard-by-video-aula';
import { formatTime } from '../../../../commom/formatTime';
import TrackEventsUser from '../../../../track/trackEventsUser';
import { useFinishMaterial } from '../../../../hooks/useFinishMaterial';
import { setContentView } from '../../../../queries/contentViews';
import { materialViewTypeFromCategory } from '../../../../models/contentView';
import { useMostraSeuAprovaStore } from '../../MostraSeuAprovaModal/store';

type TabValue = 'related' | 'comments' | 'flashcards' | 'materials';

interface UseLessonPageParams {
  id: string;
  time?: string | number;
  subjectRelated?: string;
  /** Slug da videoaula: lista também os exercícios ligados ao vídeo dela. */
  lessonSlug?: string;
  module?: string;
  courseId?: string;
  isLiveStream: boolean;
  contentType?: 'Lesson' | 'Material' | 'Live';
  showQuiz: boolean;
  /**
   * `origin` diz se a conclusão veio do fim real do vídeo (`ended`) ou do corte
   * que já o considera assistido (`virtual`, por volta de 80%). Quem não
   * precisa da distinção pode ignorar o parâmetro.
   */
  onFinish?: (origin?: 'virtual' | 'ended') => any;
}

export function useLessonPage({
  id,
  time,
  subjectRelated,
  lessonSlug,
  module,
  courseId,
  isLiveStream,
  contentType,
  showQuiz,
  onFinish,
}: UseLessonPageParams) {
  const { user } = useUserStore();
  const { shouldShowPopUpScore } = useShouldShowPopUpScore();
  const notShowBadgeScore = isLiveStream || contentType === 'Live';

  const [isQuizOpen, setIsQuizOpen] = useState(false);

  /**
   * O modal da campanha #mostraseuaprova pode estar aberto por cima da aula
   * quando ela termina. Os dois disputariam a tela, então o quiz espera o aluno
   * fechar a campanha para aparecer.
   *
   * Sai junto com a campanha — ver
   * core/components/organism/MostraSeuAprovaModal/README.md.
   */
  const isMostraSeuAprovaOpen = useMostraSeuAprovaStore(
    (state) => state.content !== null
  );
  const [isQuizWaitingCampaign, setIsQuizWaitingCampaign] = useState(false);

  const openQuiz = () => {
    // Lido do store na hora: o valor reativo acima pode estar de um render
    // anterior ao modal abrir.
    if (useMostraSeuAprovaStore.getState().content) {
      setIsQuizWaitingCampaign(true);
      return;
    }

    setIsQuizOpen(true);
  };

  useEffect(() => {
    if (!isQuizWaitingCampaign || isMostraSeuAprovaOpen) return;

    setIsQuizWaitingCampaign(false);
    setIsQuizOpen(true);
  }, [isQuizWaitingCampaign, isMostraSeuAprovaOpen]);

  const [seekTo, setSeekTo] = useState<{
    seconds: number;
    requestId: number;
  }>();
  const [activeTab, setActiveTab] = useState<TabValue>('comments');
  const [showModalScore, setShowModalScore] = useState(false);

  // Garante que a conclusão (/finished) seja registrada no máximo uma vez por
  // aula. O guard `firedVirtualFinish` vive no estado do player e é perdido se
  // ele remontar (troca de props/memoização), o que reabriria o disparo. Este
  // ref vive no hook e sobrevive a remontagens do player, evitando o segundo
  // /finished ao final do vídeo.
  const hasRegisteredFinishRef = useRef(false);

  useEffect(() => {
    hasRegisteredFinishRef.current = false;
  }, [id]);

  // Quiz
  const { data: quiz } = useFetchLessonQuiz(id, showQuiz);
  const quizRef = useRef(quiz);
  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);

  // Related content
  const {
    mutate,
    data: rawRelatedData,
    isPending: isRelatedPending,
  } = useRelatedContentAuxiliar();
  const [relatedData, setRelatedData] = useState<any>(null);

  useEffect(() => {
    if (rawRelatedData) setRelatedData(rawRelatedData);
  }, [rawRelatedData]);

  // Finish material
  const { mutate: finishMaterial } = useFinishMaterial();

  // Badge scores
  const {
    getBadgeScores,
    badgeScore,
    isPending: isPendingBadgeScores,
  } = useGetBadgeScores();

  // Flashcards
  const {
    flashcards,
    isLoading: isLoadingFlashcards,
    fetchFlashcards,
  } = useFlashcardByVideoAula();

  const [flashcardsFiltered, setFlashcardsFiltered] = useState<
    IFlashcardByVideoAulaResponse[]
  >([]);

  useEffect(() => {
    setFlashcardsFiltered(flashcards || []);
  }, [flashcards]);

  useEffect(() => {
    if (!id) return;
    fetchFlashcards(id);
  }, [id, fetchFlashcards]);

  // Conteúdo relacionado: refaz ao trocar de aula, mesmo no mesmo assunto
  // (os exercícios ligados ao vídeo são da aula).
  useEffect(() => {
    if (subjectRelated && module) {
      mutate({ module, subject: subjectRelated, lesson: lessonSlug });
    }
  }, [subjectRelated, module, lessonSlug, mutate]);

  // Initial data fetch
  useEffect(() => {
    if (user?.id) {
      getBadgeScores({
        externalId: user.id,
        externalContent: id,
        externalContentOrigin: courseId ? 'course' : 'navegation',
        externalCourseId: courseId,
      });
    }
  }, [subjectRelated, module, mutate, user?.id]);

  // Derived values
  const timeLesson = useMemo(() => formatTime(time || ''), [time]);
  const showFlashcards = user?.enableNewFeatures26 === true;
  const hasQuiz = quiz && quiz.exercises.length > 0 && showFlashcards;
  const isLive = contentType === 'Live' || isLiveStream;
  const commentTargetType =
    contentType && contentType !== 'Material'
      ? contentType
      : ('Lesson' as const);

  const changeFlashcardVinculed = (flashcardUuid: string) => {
    setFlashcardsFiltered((prev) =>
      prev.map((flashcard) => {
        if (flashcard.uuid !== flashcardUuid) return flashcard;
        return {
          ...flashcard,
          shouldReview: false,
          hasVinculed: !flashcard.shouldReview
            ? !flashcard.hasVinculed
            : flashcard.hasVinculed,
          flashcardDeckUuid: flashcard.shouldReview
            ? undefined
            : flashcard.flashcardDeckUuid,
        };
      })
    );
  };

  const handleFinish = async (origin?: 'virtual' | 'ended') => {
    // Ao término do vídeo ('ended') apenas abrimos o quiz. A conclusão
    // (/finished) NÃO é registrada novamente aqui: ela já é disparada uma única
    // vez ao atingir a marca virtual (60%). Como a rota é toggle, uma segunda
    // chamada no 'ended' reverteria o status da aula.
    if (origin === 'ended') {
      if (!isLiveStream) {
        setIsQuizOpen(true);
      }
      return;
    }

    // Idempotente: só registra a conclusão uma vez por aula (na marca virtual),
    // mesmo que o player remonte e reemita o evento 'virtual'.
    if (hasRegisteredFinishRef.current) return;
    hasRegisteredFinishRef.current = true;

    const result = await onFinish?.();

    if (user?.id && id) {
      getBadgeScores({
        externalId: user.id,
        externalContent: id,
        externalContentOrigin: courseId ? 'course' : 'navegation',
        externalCourseId: courseId,
      });
    }

    if (result?.result === 'created' && shouldShowPopUpScore) {
      setShowModalScore(true);
    }
  };

  const handleVideoEnd = () => {
    const currentQuiz = quizRef.current;
    if (showQuiz && currentQuiz && !isLiveStream) {
      openQuiz();
    }
  };

  const getScoreProps = () => {
    if (isPendingBadgeScores || notShowBadgeScore) return {};
    return {
      showScore: badgeScore?.pointAt ? badgeScore?.points : 5,
      badgeTypeScore: (badgeScore?.didPoint ? 'success' : 'warning') as
        | 'success'
        | 'warning',
    };
  };

  const dispatchEventTrackerPdfSummary = () => {
    try {
      const event = isLiveStream ? 'open_material_live' : 'open_summary_lesson';
      TrackEventsUser(event, 'v2');
    } catch (err) {
      console.log(err);
    }
  };

  const onChangeTimeline = (data: any) => {
    const seconds = Number(data?.seconds);
    if (!Number.isFinite(seconds)) return;
    // O requestId garante o salto mesmo ao clicar duas vezes no mesmo item.
    setSeekTo((prev) => ({
      seconds,
      requestId: (prev?.requestId ?? 0) + 1,
    }));
  };

  const updateRelatedFavorite = (callbackData: {
    targetId: string;
    insert?: boolean;
    category: string;
  }) => {
    if (!relatedData) return;
    const categoryToKey: Record<string, string> = {
      apostila: 'workbooks',
      audio: 'podcast',
      exercise: 'exercise',
    };
    const key = categoryToKey[callbackData.category];
    if (!key) return;

    const updated = { ...relatedData };
    if (updated[key]) {
      updated[key] = updated[key].map((item: any) =>
        item.id === callbackData.targetId
          ? { ...item, favorite: callbackData.insert === true }
          : item
      );
    }
    setRelatedData(updated);
  };

  const updateRelatedDone = (targetId: string, slug: string) => {
    if (!relatedData || !slug) return;

    // A origem dentro do player de aula depende de onde a aula foi acessada.
    const origin = courseId ? 'courses' : 'free_browsing';

    // Estado atual do item para alternar entre marcar e desmarcar como concluído.
    // A UI alterna de forma otimista; para materiais/podcasts a rota `/opened`
    // já é toggle no servidor, então `nextDone` acompanha o resultado esperado.
    const currentItem = ['workbooks', 'podcast', 'exercise']
      .flatMap((key) => relatedData[key] ?? [])
      .find((item: any) => item.id === targetId);
    const nextDone = !currentItem?.done;

    if (relatedData.exercise?.some((i: any) => i.id === targetId)) {
      // Exercício relacionado: define explicitamente o status (não é toggle no
      // servidor), marcando como visto sem registrar resposta errada.
      setContentView({
        contentType: 'exercise',
        contentId: targetId,
        viewed: nextDone,
        origin,
      });
    } else if (relatedData.podcast?.some((i: any) => i.id === targetId)) {
      finishMaterial({ slug, type: 'podcast', origin });
    } else {
      const item = relatedData.workbooks?.find((i: any) => i.id === targetId);
      finishMaterial({
        slug,
        type: materialViewTypeFromCategory(item?.file_category),
        origin,
      });
    }

    const updated = { ...relatedData };
    ['workbooks', 'podcast', 'exercise'].forEach((key) => {
      if (updated[key]) {
        updated[key] = updated[key].map((item: any) =>
          item.id === targetId ? { ...item, done: nextDone } : item
        );
      }
    });
    setRelatedData(updated);
  };

  return {
    // State
    isQuizOpen,
    setIsQuizOpen,
    seekTo,
    activeTab,
    setActiveTab,
    showModalScore,
    setShowModalScore,

    // Data
    quiz,
    relatedData,
    isRelatedPending,
    flashcardsFiltered,
    isLoadingFlashcards,
    fetchFlashcards,

    // Derived
    timeLesson,
    showFlashcards,
    hasQuiz,
    isLive,
    commentTargetType,
    scoreProps: getScoreProps(),

    // Actions
    handleFinish,
    handleVideoEnd,
    updateRelatedFavorite,
    updateRelatedDone,
    changeFlashcardVinculed,
    dispatchEventTrackerPdfSummary,
    onChangeTimeline,
  };
}
